import React from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Paintbrush, Hammer, Trees, Zap, Layers, Building, Wrench, Droplets, Home as HomeIcon,
  ArrowRight, Check, AlertTriangle, Phone, ArrowUpFromLine
} from 'lucide-react';

const serviceCategories = [
  {
    id: 'decorating',
    name: 'Decorating',
    icon: Paintbrush,
    color: 'from-rose-500 to-pink-600',
    description: 'Professional painting and wallpapering services',
    services: [
      'Interior painting & wallpapering by qualified decorators',
      'Preparation & finishing for walls, ceilings, and woodwork',
      'Minor plaster repairs before painting, holes & cracks etc',
      'Feature walls and accent painting',
      'Woodwork painting and staining',
      'Fence panel & shed painting',
      'Painting & decorating (residential & commercial)'
    ]
  },
  {
    id: 'outdoors',
    name: 'Outdoors & Garden',
    icon: Trees,
    color: 'from-green-500 to-emerald-600',
    description: 'Complete outdoor and garden maintenance',
    services: [
      'Shed & garden building assembly – Pergola, man cave & gazebo installation',
      'Landscaping including paving, patios, paths & walling',
      'Turf laying & artificial grass installation',
      'Fencing, railings & gates – supply, fitting & repairs',
      'Decking installation & refurbishment',
      'Shed repairs & roof felt replacement',
      'Garden clearance & waste removal',
      'Exterior wood treatment & staining (fences, decking, sheds)',
      'Cleaning & pressure washing of patios, driveways & outdoor surfaces',
      'Paving – jetwashing, re-sanding & sealing'
    ]
  },
  {
    id: 'building',
    name: 'Building & Maintenance',
    icon: Building,
    color: 'from-slate-500 to-gray-600',
    description: 'General building and property maintenance',
    services: [
      'Plastering & rendering',
      'Loft insulation, hatches, ladders & boarding',
      'Driveways & paving repairs or installation',
      'Fascia, soffits & guttering cleaning/repairs/replacement',
      'Internal partition walls & stud work',
      'Cladding & paneling installation',
      'Garage conversions',
      'All other general building & property maintenance tasks'
    ]
  },
  {
    id: 'carpentry',
    name: 'Timber & Joinery',
    icon: Hammer,
    color: 'from-amber-600 to-orange-700',
    description: 'Skilled carpentry and joinery work',
    services: [
      'Internal door fitting & repairs (door hanging)',
      'Skirting board & architrave installation',
      'Shelving installation (custom or standard)',
      'Bespoke woodwork for storage & boxing in',
      'Cladding & paneling installation',
      'Bedrooms – modular, sliding wardrobes & fitted furniture',
      'Kitchen & bathroom unit assembly & fitting',
      'Flat pack & freestanding furniture assembly'
    ]
  },
  {
    id: 'plumbing',
    name: 'Plumbing',
    icon: Droplets,
    color: 'from-blue-500 to-cyan-600',
    description: 'General plumbing repairs and installations',
    services: [
      'Fixing dripping taps, toilet cistern fitting & minor pipe leaks',
      'Installing/replacing dishwashers & washing machines',
      'Washing machine water valves etc',
      'Replacing taps, shower heads & basins',
      'Outdoor tap installation',
      'Radiator replacement'
    ],
    note: 'Major plumbing works, boiler servicing and gas work requires a certified specialist – we can refer you to Expert-TradesDirect'
  },
  {
    id: 'electrics',
    name: 'Electrics',
    icon: Zap,
    color: 'from-yellow-500 to-amber-600',
    description: 'Light electrical work and installations',
    services: [
      'Replacing light fittings, switches & plug sockets',
      'Installing new lighting fixtures',
      'Outdoor lighting & sockets',
      'Smoke & heat alarm installation/replacement'
    ],
    note: 'Installing extra plug sockets, full rewiring and consumer unit work requires a certified electrician – we can refer you to Expert-TradesDirect'
  },
  {
    id: 'general',
    name: 'General Tasks',
    icon: Wrench,
    color: 'from-indigo-500 to-violet-600',
    description: 'Everyday repairs and installations',
    services: [
      'Curtain pole & blind fitting',
      'TV wall mounting & cable concealment',
      'Door lock fitting/replacement (including security upgrades)',
      'Picture & mirror hanging',
      'Sealant replacement in bathrooms, kitchens & windows',
      'Gate & fence repairs & installation',
      'Wall mounting (TVs, shelves, pictures)',
      'Shed, gazebo, sun houses, man cave assembly',
      'General home maintenance'
    ]
  },
  {
    id: 'loft',
    name: 'Loft Services',
    icon: ArrowUpFromLine,
    color: 'from-sky-500 to-cyan-600',
    description: 'Loft storage, insulation and access solutions',
    services: [
      'Loft hatch installation',
      'Loft hatch enlargement',
      'Loft boarding for storage',
      'Loft insulation installation',
      'Loft lighting installation',
      'Loft ladder fitting',
      'Raised flooring',
      'Storage solutions & shelving',
      'Loft access improvements',
      'Insulation top-ups',
      'Safe walkways & platforms'
    ]
  },
  {
    id: 'plastering',
    name: 'Plastering & Tiling',
    icon: Layers,
    color: 'from-purple-500 to-violet-600',
    description: 'Wall, ceiling and floor finishing',
    services: [
      'Wall & ceiling plastering',
      'Rendering (internal and external)',
      'Floor tiling – kitchens, bathrooms, hallways',
      'Wall tiling – splashbacks, shower areas',
      'Plaster repairs and patching',
      'Coving and cornice installation'
    ],
    note: 'Large-scale plastering or rendering may require a specialist – we can refer you to Expert-TradesDirect'
  }
];

const specialistServices = [
  'Electrical installations, rewiring and extra plug sockets',
  'Gas work and boiler servicing',
  'Roofing and chimney repairs',
  'Large-scale plastering or rendering',
  'Structural alterations and extensions',
  'Major plumbing works',
  'Specialist flooring, stonework, or tiling'
];

export const Services = () => {
  const navigate = useNavigate();

  return (
    <div className="animate-in">
      <SEO 
        title="Handyman Services Near Me | Door Fitting, Skirting, Painting, Plumbing & More"
        description="Full list of handyman services in Manchester & Cheshire. Door fitting & hanging, skirting boards, architraves, wall panelling, sanding, painting, decorating, plumbing, electrics, plastering. No VAT. Free estimates."
        keywords="handyman services near me, door fitting near me, door hanging Manchester, skirting board fitting near me, architrave fitting near me, wall panelling installation near me, sanding services near me, painting near me, decorating near me, plumbing near me, electrics near me, plastering near me, handyman Manchester, carpenter Cheshire, joiner near me, local tradesman, property maintenance near me"
        canonical="/services"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary to-primary/80 text-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-4 py-1.5 rounded-full bg-white/10 text-white/90 text-sm font-medium mb-6">
            Complete Service List
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 font-['Outfit'] tracking-tight">
            Everything We Can Do
          </h1>
          <p className="text-lg md:text-xl text-white/80 max-w-3xl mx-auto mb-4">
            No hidden costs – just honest, reliable work. Free estimates on all jobs.
          </p>
          <p className="text-base text-white/70 max-w-2xl mx-auto mb-8">
            Serving Greater Manchester & Cheshire
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              size="lg" 
              className="btn-accent text-lg px-8 py-6 h-auto"
              onClick={() => navigate('/quote')}
            >
              Get Free Quote <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <a 
              href="tel:07300405045"
              className="inline-flex items-center justify-center text-lg px-8 py-6 h-auto bg-white/10 hover:bg-white/20 text-white rounded-lg font-medium transition-colors"
            >
              <Phone className="mr-2 w-5 h-5" /> 07300 40 50 45
            </a>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {serviceCategories.map((category, idx) => (
              <Card key={category.id} className="overflow-hidden border-border hover:shadow-lg transition-shadow">
                <div className={`bg-gradient-to-r ${category.color} p-6 md:p-8`}>
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center">
                      <category.icon className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-bold text-white font-['Outfit']">
                        {category.name}
                      </h2>
                      <p className="text-white/80">{category.description}</p>
                    </div>
                  </div>
                </div>
                <CardContent className="p-6 md:p-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {category.services.map((service) => (
                      <div key={service} className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground">{service}</span>
                      </div>
                    ))}
                  </div>
                  {category.note && (
                    <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-amber-700">{category.note}</p>
                      </div>
                    </div>
                  )}
                  <div className="mt-6 flex justify-end">
                    <Button 
                      onClick={() => navigate(`/quote?service=${category.id}`)}
                      className="rounded-full"
                    >
                      Get Quote for {category.name} <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Landlord Services - Separate Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-indigo-600 to-blue-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center mx-auto mb-6">
                <HomeIcon className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white font-['Outfit'] tracking-tight mb-4">
                Expert Landlord Services
              </h2>
              <p className="text-lg text-white/80">
                Complete property maintenance solutions for landlords and letting agents
              </p>
            </div>

            <Card className="border-0 shadow-2xl">
              <CardContent className="p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                  {[
                    'Tenant-ready property preparation',
                    'Between-tenancy repairs and decorating',
                    'Kitchen & bathroom refreshes',
                    'Safety compliance checks',
                    'Full property maintenance packages',
                    'Emergency repair call-outs',
                    'Property inspections and reports',
                    'End-of-tenancy repairs',
                    'Inventory condition checks',
                    'Regular maintenance contracts'
                  ].map((service, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">{service}</span>
                    </div>
                  ))}
                </div>
                <div className="text-center">
                  <Button 
                    size="lg"
                    onClick={() => navigate('/quote?service=landlord')}
                    className="btn-accent px-8"
                  >
                    Get Landlord Quote <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Specialist Services Section */}
      <section className="py-16 md:py-24 bg-muted">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full bg-amber-100 text-amber-700 text-sm font-medium mb-4">
              Specialist Trades
            </span>
            <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] tracking-tight mb-4">
              When the Job Needs More...
            </h2>
            <p className="text-muted-foreground">
              Most jobs can be handled by Expert-Handyman. But when your project needs a true trade specialist, 
              we've got you covered through <strong>Expert-TradesDirect</strong>.
            </p>
          </div>

          <Card className="border-amber-200 bg-amber-50/50 max-w-4xl mx-auto">
            <CardHeader>
              <CardTitle className="text-xl font-['Outfit'] text-amber-800">
                Jobs Requiring Specialist Trades
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {specialistServices.map((service, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-amber-500" />
                    <span className="text-amber-800">{service}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6 p-4 bg-white rounded-xl border border-amber-200">
                <p className="text-sm text-muted-foreground">
                  <strong>Don't worry!</strong> If your job needs a specialist, just submit your request as normal. 
                  Our team will identify when specialist work is needed and connect you with our trusted trade network 
                  through Expert-TradesDirect. You'll receive the same great service and fair pricing.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* SEO Content Section - Keyword-rich service descriptions */}
      <section className="py-16 md:py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold mb-10 font-['Outfit'] tracking-tight text-center">
            Handyman Services Near You in Manchester & Cheshire
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div>
              <h3 className="font-semibold text-lg mb-2 font-['Outfit']">Door Fitting & Hanging Near Me</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Professional door fitting and door hanging across Manchester, Stockport, Altrincham, Sale, Wilmslow and all of Cheshire. 
                We fit internal doors from just £50 per door, including planing and adjusting for a perfect fit. Handles, locks and hinges 
                fitted from £75 per door. Whether you need a single door hung or a full house of doors fitted, we provide a fast, 
                clean and affordable service.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-lg mb-2 font-['Outfit']">Skirting Board & Architrave Fitting Near Me</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Expert skirting board and architrave installation in Manchester and Cheshire. We supply and fit or fit what you provide. 
                Our carpenters deliver precision-cut mitre joints and a neat, professional finish on all skirting boards, architraves 
                and door trims. Perfect for new builds, renovations or replacing damaged boards.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-lg mb-2 font-['Outfit']">Wall Panelling Installation Near Me</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Transform your rooms with modern wall panelling. We install all styles including panel moulding, 
                wainscoting, shaker panelling, tongue and groove, and decorative cladding. Covering Manchester, Stockport, 
                Cheadle, Didsbury, Chorlton and all surrounding areas. Free estimates on all wall panelling projects.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-lg mb-2 font-['Outfit']">Sanding Services Near Me</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Professional sanding for doors, floors, window frames, skirting boards and furniture. We prepare surfaces 
                properly before painting and finishing to ensure a flawless result. Floor sanding and refinishing also available 
                across Greater Manchester and Cheshire.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-lg mb-2 font-['Outfit']">Painting & Decorating Near Me</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Interior and exterior painting and decorating in Manchester, Cheshire, Stockport, Altrincham, Wilmslow 
                and Macclesfield. Walls, ceilings, woodwork, doors, fences and more. We also offer wallpapering, 
                feature walls and full room makeovers. From £35/hour with no VAT — save 30-60% vs agencies.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-lg mb-2 font-['Outfit']">Plumbing Services Near Me</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                General plumbing repairs and installations across Manchester and Cheshire. Dripping taps, toilet repairs, 
                radiator replacements, washing machine connections, tap and shower replacements. Fast response, affordable rates 
                and no VAT charged. For major plumbing work we refer to certified specialists.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-lg mb-2 font-['Outfit']">Electrics Near Me</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Light electrical work in Manchester and Cheshire. Replacing light fittings, switches and plug sockets. 
                Outdoor lighting, garden electrics and smoke alarm installation. Safe, professional work at affordable 
                rates. For full rewiring and certification work, we refer to qualified electricians.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-lg mb-2 font-['Outfit']">Plastering Near Me</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Wall and ceiling plastering, plaster repairs, patching, skim coating and rendering across Greater Manchester 
                and Cheshire. We also offer floor and wall tiling for kitchens, bathrooms and hallways. Coving and cornice 
                installation available. No VAT charged on any plastering work.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-lg mb-2 font-['Outfit']">Local Handyman Near Me</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Your reliable local handyman covering Manchester, Stockport, Sale, Altrincham, Wilmslow, Macclesfield, 
                Cheadle, Bramhall, Didsbury, Chorlton, Salford, Trafford, Bolton, Bury, Warrington and Knutsford. 
                TV mounting, shelving, flat-pack assembly, curtain poles, locks, property maintenance and more. 
                No VAT, no hidden costs — just honest work at fair prices.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-primary to-primary/80 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 font-['Outfit'] tracking-tight">
            Not Sure What You Need?
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Just describe your problem and we'll connect you with the right service.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              size="lg" 
              className="btn-accent text-lg px-8 py-6 h-auto"
              onClick={() => navigate('/quote')}
            >
              Get Free Quote <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <a 
              href="tel:07300405045"
              className="inline-flex items-center justify-center text-lg px-8 py-6 h-auto bg-white/10 hover:bg-white/20 text-white rounded-lg font-medium transition-colors"
            >
              <Phone className="mr-2 w-5 h-5" /> Call Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
